import { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Calculator, TrendingUp, DollarSign, Users, Minus, Plus } from 'lucide-react';

const ROICalculator = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const [clients, setClients] = useState(20);
  const [hourlyRate, setHourlyRate] = useState(50);
  const [consultationPrice, setConsultationPrice] = useState(80);

  // Calculations
  const hoursPerClient = 2; // Hours saved per client per month
  const retentionIncrease = 0.25; // 25% more retention
  const currentRetention = 0.30; // 30% without HabitIA
  const newRetention = currentRetention + retentionIncrease;

  const hoursSaved = clients * hoursPerClient;
  const timeValueSaved = hoursSaved * hourlyRate;
  const extraClientsRetained = Math.round(clients * retentionIncrease);
  const revenueFromRetention = extraClientsRetained * consultationPrice * 2; // 2 consultations extra
  const totalValue = timeValueSaved + revenueFromRetention;
  const habitIACost = 29;
  const roi = Math.round(((totalValue - habitIACost) / habitIACost) * 100);

  const adjustValue = (setter, current, delta, min, max) => {
    const newValue = current + delta;
    if (newValue >= min && newValue <= max) {
      setter(newValue);
    }
  };

  return (
    <section ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="roi-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#849B89] mb-4">
            Calcula tu ROI
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight mb-4">
            ¿Cuánto vale tu tiempo
            <span className="text-[#2E5C41]"> recuperado?</span>
          </h2>
          <p className="text-base md:text-lg text-[#4B5E53] leading-relaxed">
            Ajusta los valores según tu práctica y descubre el retorno real de HabitIA.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
          {/* Calculator Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Clients */}
            <div className="p-6 bg-[#FAFAF9] rounded-2xl border border-[#2E5C41]/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#2E5C41]" strokeWidth={1.5} />
                  <span className="font-medium text-[#1A2E24]">Pacientes activos</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustValue(setClients, clients, -5, 5, 100)}
                    className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center hover:bg-[#2E5C41]/20 transition-colors"
                    data-testid="clients-minus"
                  >
                    <Minus className="w-4 h-4 text-[#2E5C41]" />
                  </button>
                  <span className="w-12 text-center font-cabinet font-bold text-xl text-[#1A2E24]">
                    {clients}
                  </span>
                  <button
                    onClick={() => adjustValue(setClients, clients, 5, 5, 100)}
                    className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center hover:bg-[#2E5C41]/20 transition-colors"
                    data-testid="clients-plus"
                  >
                    <Plus className="w-4 h-4 text-[#2E5C41]" />
                  </button>
                </div>
              </div>
              <input
                type="range"
                min="5"
                max="100"
                value={clients}
                onChange={(e) => setClients(parseInt(e.target.value))}
                className="w-full h-2 bg-[#2E5C41]/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[#2E5C41] [&::-webkit-slider-thumb]:rounded-full"
                data-testid="clients-slider"
              />
            </div>

            {/* Hourly Rate */}
            <div className="p-6 bg-[#FAFAF9] rounded-2xl border border-[#2E5C41]/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-[#2E5C41]" strokeWidth={1.5} />
                  <span className="font-medium text-[#1A2E24]">Valor de tu hora (USD)</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustValue(setHourlyRate, hourlyRate, -10, 20, 200)}
                    className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center hover:bg-[#2E5C41]/20 transition-colors"
                    data-testid="hourly-minus"
                  >
                    <Minus className="w-4 h-4 text-[#2E5C41]" />
                  </button>
                  <span className="w-16 text-center font-cabinet font-bold text-xl text-[#1A2E24]">
                    ${hourlyRate}
                  </span>
                  <button
                    onClick={() => adjustValue(setHourlyRate, hourlyRate, 10, 20, 200)}
                    className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center hover:bg-[#2E5C41]/20 transition-colors"
                    data-testid="hourly-plus"
                  >
                    <Plus className="w-4 h-4 text-[#2E5C41]" />
                  </button>
                </div>
              </div>
              <input
                type="range"
                min="20"
                max="200"
                step="10"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(parseInt(e.target.value))}
                className="w-full h-2 bg-[#2E5C41]/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[#2E5C41] [&::-webkit-slider-thumb]:rounded-full"
                data-testid="hourly-slider"
              />
            </div>

            {/* Consultation Price */}
            <div className="p-6 bg-[#FAFAF9] rounded-2xl border border-[#2E5C41]/10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Calculator className="w-5 h-5 text-[#2E5C41]" strokeWidth={1.5} />
                  <span className="font-medium text-[#1A2E24]">Precio por consulta (USD)</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustValue(setConsultationPrice, consultationPrice, -10, 30, 300)}
                    className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center hover:bg-[#2E5C41]/20 transition-colors"
                    data-testid="consultation-minus"
                  >
                    <Minus className="w-4 h-4 text-[#2E5C41]" />
                  </button>
                  <span className="w-16 text-center font-cabinet font-bold text-xl text-[#1A2E24]">
                    ${consultationPrice}
                  </span>
                  <button
                    onClick={() => adjustValue(setConsultationPrice, consultationPrice, 10, 30, 300)}
                    className="w-8 h-8 rounded-full bg-[#2E5C41]/10 flex items-center justify-center hover:bg-[#2E5C41]/20 transition-colors"
                    data-testid="consultation-plus"
                  >
                    <Plus className="w-4 h-4 text-[#2E5C41]" />
                  </button>
                </div>
              </div>
              <input
                type="range"
                min="30"
                max="300"
                step="10"
                value={consultationPrice}
                onChange={(e) => setConsultationPrice(parseInt(e.target.value))}
                className="w-full h-2 bg-[#2E5C41]/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[#2E5C41] [&::-webkit-slider-thumb]:rounded-full"
                data-testid="consultation-slider"
              />
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            {/* ROI Card */}
            <div className="p-8 bg-[#2E5C41] rounded-3xl text-white">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-6 h-6 text-[#849B89]" strokeWidth={1.5} />
                <span className="text-sm font-medium text-white/80">Tu retorno mensual</span>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-white/80">Horas recuperadas</span>
                  <span className="font-cabinet font-bold text-2xl">{hoursSaved} hrs</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-white/80">Valor del tiempo ahorrado</span>
                  <span className="font-cabinet font-bold text-2xl">${timeValueSaved.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-white/80">Pacientes extra retenidos</span>
                  <span className="font-cabinet font-bold text-2xl">+{extraClientsRetained}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-white/80">Ingresos por retención</span>
                  <span className="font-cabinet font-bold text-2xl">${revenueFromRetention.toLocaleString()}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/20">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Valor total generado</span>
                  <span className="font-cabinet font-bold text-3xl text-[#D4F5E0]">${totalValue.toLocaleString()}</span>
                </div>
                <div className="mt-2 flex justify-between items-center text-sm">
                  <span className="text-white/60">Inversión HabitIA</span>
                  <span className="text-white/80">- ${habitIACost}/mes</span>
                </div>
              </div>
            </div>

            {/* ROI Badge */}
            <div className="p-6 bg-[#D4F5E0] rounded-2xl text-center">
              <p className="text-sm text-[#2E5C41] mb-2">Retorno de inversión</p>
              <p className="font-cabinet font-bold text-5xl text-[#1A2E24]">{roi}x</p>
              <p className="text-xs text-[#4B5E53] mt-2">
                Por cada $1 invertido, recuperas ${(roi + 1).toFixed(0)}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
